import { CUSTOMER_DOCUMENT_CATEGORIES, DEFAULT_DOCUMENT_CATEGORY, DEFAULT_DOCUMENT_TEMPLATE } from "@customer-config"
import { SUPPORTED_LOCALES_TYPES } from "@locales/locales-types"
import { DynamicTranslate } from "@services/i18n/i18n"

import { DocumentCategory, DocumentTemplateCategoryName } from "../models/categories"
import { IChapter, ChecklistItemState } from "../models/chapter"
import { DocumentTemplateHelper } from "../models/templates"

/**
 * Structure of the return value of the checklistState function
 */
interface IChecklistMetaData {
  /**
   * true, if all non-ignored elements are done
   */
  allDone: boolean
  /**
   * Number of items of the checklist of the template of the chapter.
   */
  templateChecklistLength: number
  /**
   * Number of user generated checklist items of the chapter.
   */
  userChecklistLength: number
  /**
   * Number of entries that are marked as done.
   */
  doneEntries: number
  /**
   * Number of entries that are marked as ignored.
   */
  ignoredEntries: number
  /**
   * Number of entries that are open:
   * they are marked as open
   * or are part of the template checklist but have not yet been stored in the database.
   */
  openEntries: number
  /**
   * Number of entries that are open or done.
   */
  nonIgnoredEntries: number
}


export class DocumentHelper {

  /**
   * Calculates the state of a checklist.
   *
   * @param chapter a chapter to be checked for a finished checklist
   * @param t DynamicTranslate to be used to load the chapter template
   * @returns a number of calculated data of the checklist
   */
  public static checklistState(chapter: IChapter, t: DynamicTranslate, locale: SUPPORTED_LOCALES_TYPES): IChecklistMetaData {
    const chapterTemplate = DocumentTemplateHelper.getChapterTemplate(
      chapter?.template,
      t,
      locale
    )
    const templateChecklistLength = chapterTemplate?.checklist?.length ?? 0
    const userChecklistLength = chapter?.checklist?.filter(entry => entry.type === 'user').length ?? 0
    const doneEntries = chapter?.checklist?.filter(entry => entry.state === ChecklistItemState.Done).length ?? 0
    const ignoredEntries = chapter?.checklist?.filter(entry => entry.state === ChecklistItemState.Ignored).length ?? 0

    const nonIgnoredEntries = templateChecklistLength + userChecklistLength - ignoredEntries
    const allDone = doneEntries === nonIgnoredEntries

    return {
      allDone,
      templateChecklistLength,
      userChecklistLength,
      doneEntries,
      ignoredEntries,
      nonIgnoredEntries,
      // NOTE: not all entries of the chapter checklist may be in the database!
      // They may be known client-side because of the template data, but may be not written to the backend
      // because they never have been checked/unchecked.
      // Those items are open.
      openEntries: templateChecklistLength + userChecklistLength - ignoredEntries - doneEntries
    }
  }

  /**
   * freshDocumentCategory delivers an initial document category.
   *
   * Set the category default if none is given
   * to the presetCategory if one is given
   * or to the configured default document category if given
   * or to "Other" by default
   *
   * @param presetCategory optional preset category
   * @returns inital category value
   */
  public static freshDocumentCategory = (presetCategory?: DocumentCategory): DocumentCategory =>
    presetCategory ?? DEFAULT_DOCUMENT_CATEGORY ?? DocumentCategory.Other

  /**
   * Use customer config values for creating the available category options
   * but always add/keep Other as category.
   */
  public static availableDocumentCategories = (): DocumentCategory[] => {
    const availableDocumentCategories: DocumentCategory[] = CUSTOMER_DOCUMENT_CATEGORIES ?? []
    if (!availableDocumentCategories.includes(DocumentCategory.Other)) {
      availableDocumentCategories.push(DocumentCategory.Other)
    }

    return availableDocumentCategories
  }

  /**
   * freshDocumentTemplate delivers a default template if the client config provides one
   * that is known/valid and matches the preset document category
   * to be used as an standard fresh document template.
   *
   * @param category of the document
   * @returns default document template or undefined
   */
  public static freshDocumentTemplate = (category: DocumentCategory): DocumentTemplateCategoryName => DocumentTemplateHelper.templateMatchesCategory(
    category,
    DEFAULT_DOCUMENT_TEMPLATE as DocumentTemplateCategoryName
  )
    ? DEFAULT_DOCUMENT_TEMPLATE as DocumentTemplateCategoryName
    : undefined
}